// src/components/authcontext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  canAccess as evaluateAccess,
  getPrimaryRoleLabel,
  getUserPermissions,
  getUserRoles,
} from "../utils/rbac";

const AuthContext = createContext();

const parseStoredUser = () => {
  const candidates = [localStorage.getItem("authUser"), localStorage.getItem("user")];

  for (const rawValue of candidates) {
    if (!rawValue) continue;

    try {
      const parsed = JSON.parse(rawValue);
      return parsed?.data || parsed;
    } catch (error) {
      console.error("Failed to parse stored auth user:", error);
    }
  }

  return null;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [user, setUser] = useState(parseStoredUser);

  const roles = useMemo(() => getUserRoles(user), [user]);
  const permissions = useMemo(() => getUserPermissions(user), [user]);
  const primaryRoleLabel = useMemo(() => getPrimaryRoleLabel(user), [user]);
  const isAuthenticated = Boolean(token);

  // Keep token in localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem("authToken", token);
    } else {
      localStorage.removeItem("authToken");
    }
  }, [token]);

  // Keep user in localStorage
  useEffect(() => {
    if (user) {
      const serialized = JSON.stringify(user);
      localStorage.setItem("user", serialized);
      localStorage.setItem("authUser", serialized);
      if (roles[0]) {
        localStorage.setItem("role", roles[0]);
      }
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("authUser");
      localStorage.removeItem("role");
    }
  }, [roles, user]);

  // 🔥 Auto-fetch user profile when token is set but no user loaded
  useEffect(() => {
    const fetchProfile = async () => {
      if (token && !user) {
        try {
          const res = await fetch("https://care-api.equily.ng/api/user", {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          });

          if (!res.ok) throw new Error("Failed to fetch user profile");

          const data = await res.json();
          const profile = data?.data || data;
          setUser(profile);
          console.log("Fetched user from /api/user:", profile);
        } catch (err) {
          console.error("❌ Error fetching profile:", err);
          setUser(null);
          localStorage.removeItem("user");
          localStorage.removeItem("authUser");
          localStorage.removeItem("role");
        }
      }
    };
    fetchProfile();
  }, [token, user]);

  const login = (token, user = null) => {
    setToken(token);
    if (user) {
      setUser(user?.data || user);
    }
  };

  const hasRole = useCallback(
    (allowedRoles) =>
      evaluateAccess(user, {
        allowedRoles: Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles],
      }),
    [user],
  );

  const hasPermission = useCallback(
    (requiredPermissions) =>
      evaluateAccess(user, {
        requiredPermissions: Array.isArray(requiredPermissions)
          ? requiredPermissions
          : [requiredPermissions],
      }),
    [user],
  );

  const canAccess = useCallback(
    (config) => evaluateAccess(user, config),
    [user],
  );

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("authUser");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("role");
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        roles,
        permissions,
        primaryRoleLabel,
        isAuthenticated,
        login,
        logout,
        hasRole,
        hasPermission,
        canAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
